import { ReactNode, useState } from "react";
import { Box, Button } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";

import {
  BotNote,
  BotNoteLink,
  useSaveBotNoteMutation,
} from "features/adminUsers/adminUsers";

import BotNoteEditDialog from "./BotNoteEditDialog";
import BotNoteView, { hasNoteContent } from "./BotNoteView";

export type BotNoteScope = "vendor" | "bot";

export interface BotNoteTarget {
  scope: BotNoteScope;
  key: string;
  title: string;
  note: BotNote | null;
}

/** The edit dialog and its save, shared by every place a note is changed. */
export function useBotNoteEditor(): {
  open: (target: BotNoteTarget) => void;
  dialog: ReactNode;
} {
  const [save, saving] = useSaveBotNoteMutation();
  const [editing, setEditing] = useState<BotNoteTarget | null>(null);
  const [error, setError] = useState<string | null>(null);

  const commit = async (
    text: string,
    links: BotNoteLink[],
  ): Promise<boolean> => {
    if (!editing) return false;
    setError(null);
    try {
      await save({
        scope: editing.scope,
        key: editing.key,
        text,
        links,
      }).unwrap();
      setEditing(null);
      return true;
    } catch (e) {
      const said = (e as { data?: { message?: string } })?.data?.message;
      setError(said || "The note was not saved.");
      return false;
    }
  };

  return {
    open: (target) => {
      setError(null);
      setEditing(target);
    },
    dialog: (
      <BotNoteEditDialog
        open={editing != null}
        title={editing?.title ?? ""}
        note={editing?.note}
        saving={saving.isLoading}
        error={error}
        onClose={() => setEditing(null)}
        onSave={commit}
      />
    ),
  };
}

/**
 * A vendor's or a bot's note where the bots are run, with Edit on it for
 * whoever may change it (CAL-6162). With offerAdd a missing note leaves an
 * Add note button in its place.
 */
export default function EditableBotNote({
  scope,
  noteKey,
  note,
  label,
  canEdit,
  offerAdd = false,
  sx,
}: {
  scope: BotNoteScope;
  /** The vendor key or the bot id; empty while the catalogue does not know it. */
  noteKey: string | null | undefined;
  note: BotNote | null | undefined;
  label: string;
  canEdit: boolean;
  offerAdd?: boolean;
  sx?: SxProps<Theme>;
}) {
  const editor = useBotNoteEditor();
  const editable = canEdit && !!noteKey;
  const exists = hasNoteContent(note);
  if (!exists && !(editable && offerAdd)) return <>{editor.dialog}</>;

  const edit = () =>
    editor.open({
      scope,
      key: noteKey ?? "",
      title: label || noteKey || "",
      note: exists ? (note as BotNote) : null,
    });

  return (
    <Box sx={sx}>
      {exists ? (
        <BotNoteView
          note={note}
          label={label}
          dense
          onEdit={editable ? edit : undefined}
        />
      ) : (
        <Button
          size="small"
          startIcon={<NoteAddOutlinedIcon fontSize="small" />}
          onClick={edit}
        >
          Add note for {label}
        </Button>
      )}
      {editor.dialog}
    </Box>
  );
}
